/** Combos y recetas (spec 20): consultas y mutaciones de la configuración, y el cálculo para el carrito. */
import type { CalculoComboDTO, ComboDTO, ComboEntrada, ComboResumenDTO, Pagina } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put } from '@/lib/api.ts';

export interface FiltrosCombos {
  q?: string | undefined;
  activo?: 'true' | 'false' | 'todos' | undefined;
  pagina?: number | undefined;
  limite?: number | undefined;
}

export const clavesCombos = {
  lista: (f: FiltrosCombos) => ['combos', 'lista', f] as const,
  detalle: (id: string) => ['combos', 'detalle', id] as const,
};

export const useCombos = (f: FiltrosCombos, habilitado = true) =>
  useQuery({ queryKey: clavesCombos.lista(f), queryFn: () => get<Pagina<ComboResumenDTO>>('/combos', f as Record<string, string | number | undefined>), enabled: habilitado, placeholderData: (p) => p });
export const useCombo = (id: string | null) => useQuery({ queryKey: clavesCombos.detalle(id ?? ''), queryFn: () => get<ComboDTO>(`/combos/${id}`), enabled: !!id });

/** Renglones del combo para una medida. `vender`: además exige que esté activo y con todos sus productos activos. */
export const calcularCombo = (id: string, medida: string, vender: boolean) => post<CalculoComboDTO>(`/combos/${id}/calcular${vender ? '?vender=true' : ''}`, { medida });

function useInvalidarCombos() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['combos'] });
}

export function useGuardarCombo() {
  const inv = useInvalidarCombos();
  return useMutation({
    mutationFn: ({ id, datos }: { id: string | null; datos: ComboEntrada }) => (id ? put<ComboDTO>(`/combos/${id}`, datos) : post<ComboDTO>('/combos', datos)),
    onSuccess: () => inv(),
  });
}

export function useActivarCombo() {
  const inv = useInvalidarCombos();
  return useMutation({ mutationFn: ({ id, activo }: { id: string; activo: boolean }) => post<ComboDTO>(`/combos/${id}/${activo ? 'activar' : 'desactivar'}`), onSuccess: () => inv() });
}

export function useDuplicarCombo() {
  const inv = useInvalidarCombos();
  return useMutation({ mutationFn: ({ id, codigo, nombre }: { id: string; codigo: string; nombre: string }) => post<ComboDTO>(`/combos/${id}/duplicar`, { codigo, nombre }), onSuccess: () => inv() });
}
